import { compact, capitalize } from 'lodash-es';
import { TableList, Typography, TableListActions, DropdownMenuItem } from 'seagull';
import { AnchorIconGrayscale, Edit2Liner24, TrashLine, HeartLine } from 'seagull/icons';
import EnterAccountantAccessButton from "../../components/EnterAccountantAccessButton/EnterAccountantAccessButton";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { AccountingMappingIndication } from "../../integrations/Accounting/AccountingMappingIndication/AccountingMappingIndication";
import { ContactAccountantAccess } from "../../models/ContactModel";
import { Accounting } from "../../types/api.model";
import { getAccountantAccessItem } from "./ContactsDashboard.utils";
import styles from "./ContactsDashboardRow.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export default function ContactsDashboardRow(props) {
  const {
    contact,
    isQboIntegrated,
    isXeroIntegrated,
    qboCustomerIdToDisplay,
    xeroCustomerIdToDisplay,
    openContactModal,
    onRequestAccountantAccess,
    onCancelAccountantAccessRequest,
    onRemoveAccountantAccess,
    onGotoClientPortal,
    onRestoreContact,
    onArchiveContact,
    isUpdateInProgress = false
  } = props;
  const accountantAccessItem = getAccountantAccessItem(contact.accountantAccess());
  const qboCustomerDisplay = contact.integratedId({
    accounting: Accounting.QBO
  }) ? qboCustomerIdToDisplay[contact.integratedId({
    accounting: Accounting.QBO
  })] : undefined;
  const xeroCustomerDisplay = contact.integratedId({
    accounting: Accounting.Xero
  }) ? xeroCustomerIdToDisplay[contact.integratedId({
    accounting: Accounting.Xero
  })] : undefined;
  const {
    XERO_INTEGRATION,
    ALLOW_EDIT_CONTACT
  } = useFeatureFlags();
  const getEditButtonTooltip = () => {
    if (isUpdateInProgress) {
      return {
        title: 'Update in progress. Try again shortly.'
      };
    }
    if (contact.isEditable() || ALLOW_EDIT_CONTACT) {
      return undefined;
    }
    return {
      title: 'As this client is already an active member of Anchor, their contact details can only be modified by them'
    };
  };
  return /*#__PURE__*/_jsx(TableList.Row, {
    "data-testid": contact.isSample() ? 'sample-contact' : "contact-row",
    "data-heapid": contact.isSample() ? 'sample-contact' : 'contact',
    items: compact([{
      value: /*#__PURE__*/_jsxs("div", {
        className: styles.companyName,
        children: [/*#__PURE__*/_jsx(Typography, {
          ellipsis: true,
          weight: "normal",
          "data-testid": "contact-company-name",
          children: contact.companyName()
        }), contact.accountantAccess() === ContactAccountantAccess.Invited && /*#__PURE__*/_jsx(EnterAccountantAccessButton, {
          variant: "pending",
          testid: "acc-access-invited-indicator"
        }), contact.accountantAccess() === ContactAccountantAccess.Active && /*#__PURE__*/_jsx(EnterAccountantAccessButton, {
          testid: "acc-access-active-indicator",
          onClick: () => onGotoClientPortal(contact)
        }), isQboIntegrated && /*#__PURE__*/_jsx(AccountingMappingIndication, {
          display: qboCustomerDisplay,
          purpose: "customer",
          accounting: Accounting.QBO
        }), isXeroIntegrated && XERO_INTEGRATION && /*#__PURE__*/_jsx(AccountingMappingIndication, {
          display: xeroCustomerDisplay,
          purpose: "customer",
          accounting: Accounting.Xero
        })]
      })
    }, {
      value: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(Typography, {
          ellipsis: true,
          weight: "normal",
          "data-testid": "contact-status",
          children: capitalize(contact.status())
        })
      })
    }, {
      value: /*#__PURE__*/_jsx(Typography, {
        ellipsis: true,
        "data-testid": "contact-contact-name",
        children: "".concat(contact.firstName(), " ").concat(contact.lastName())
      })
    }, {
      value: /*#__PURE__*/_jsxs("div", {
        "data-testid": "contact-contact-details",
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          ellipsis: true,
          children: contact.email()
        }), /*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          skin: "secondary",
          children: contact.phone()
        })]
      })
    }]),
    actionColumn: /*#__PURE__*/_jsx(TableListActions, {
      testId: contact.isSample() ? 'sample-contact-actions' : 'table-list-actions',
      btnHeapId: contact.isSample() ? 'sample-contact-view-btn' : 'contact-view-btn',
      onClickButton: () => openContactModal(contact),
      children: compact([!contact.isSample() && /*#__PURE__*/_jsx(DropdownMenuItem, {
        iconStart: /*#__PURE__*/_jsx(Edit2Liner24, {}),
        onClick: () => openContactModal(contact),
        "data-testid": "table-list-actions-menu-item",
        disabled: isUpdateInProgress || (ALLOW_EDIT_CONTACT ? false : !contact.isEditable()),
        tooltipProps: getEditButtonTooltip(),
        children: "Edit"
      }, "view"), contact.isActive() ? /*#__PURE__*/_jsx(DropdownMenuItem, {
        iconStart: /*#__PURE__*/_jsx(TrashLine, {}),
        onClick: () => onArchiveContact(contact),
        "data-testid": "actions-archive",
        disabled: contact.hasSignedAgreements() || contact.isSample(),
        skin: "error",
        tooltipProps: contact.hasSignedAgreements() ? {
          title: 'Contacts with non terminated agreements cannot be archived',
          placement: 'bottom'
        } : {},
        children: "Archive"
      }, "archive") : /*#__PURE__*/_jsx(DropdownMenuItem, {
        iconStart: /*#__PURE__*/_jsx(HeartLine, {}),
        onClick: () => onRestoreContact(contact),
        "data-testid": "actions-restore",
        children: "Restore"
      }, "restore"), accountantAccessItem && /*#__PURE__*/_jsx(DropdownMenuItem, {
        skin: accountantAccessItem.skin,
        "data-testid": "acc-access-".concat(accountantAccessItem.id),
        iconStart: /*#__PURE__*/_jsx(AnchorIconGrayscale, {}),
        onClick: () => {
          if (accountantAccessItem.id === 'request') {
            contact.customerId() && onRequestAccountantAccess(contact);
          } else if (accountantAccessItem.id === 'cancel') {
            onCancelAccountantAccessRequest(contact);
          } else {
            onRemoveAccountantAccess(contact);
          }
        },
        children: accountantAccessItem.text
      }, "accountant")])
    })
  }, contact.id());
}